import { useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Keyboard } from 'lucide-react';
import {
  ShortcutContext,
  useShortcutRegistryState,
  useKeyboardShortcut,
  useShortcutRegistry,
  isEditableTarget,
  KeyboardShortcut,
} from '@hooks/useKeyboardShortcuts';
import { createNavSequence } from '@hooks/navSequence';

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}

export function KeyboardShortcutsProvider({ children }: KeyboardShortcutsProviderProps) {
  const registry = useShortcutRegistryState();

  return (
    <ShortcutContext.Provider value={registry}>
      <GlobalShortcuts />
      {children}
      <ShortcutsHelpDialog />
    </ShortcutContext.Provider>
  );
}

function GlobalShortcuts() {
  const navigate = useNavigate();
  const { setHelpOpen, registerShortcut, unregisterShortcut } = useShortcutRegistry();

  // Show help dialog
  useKeyboardShortcut('?', () => setHelpOpen(true));
  useKeyboardShortcut('shift+/', () => setHelpOpen(true));

  // Navigation shortcuts — Gmail-style "g then <key>" sequences.
  // These MUST be a two-key sequence: registering them as `g+d` collapsed to the
  // bare key `d`, so a single keystroke matching a sidebar first-letter (d/i/t/
  // c/k/s) navigated away — even mid-typing in a dropdown. The sequence + the
  // isEditableTarget guard keep navigation deliberate and out of form controls.
  useEffect(() => {
    const seq = createNavSequence({
      d: '/dashboard',
      i: '/inbox',
      t: '/tickets',
      c: '/customers',
      k: '/knowledge',
      s: '/settings',
    });

    const handler = (event: KeyboardEvent) => {
      // Let real modifier combos (Ctrl/Cmd/Alt shortcuts) through untouched.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // Never hijack keystrokes while typing in a field or selecting in a dropdown.
      if (isEditableTarget(event.target)) return;

      const path = seq.press(event.key.toLowerCase(), Date.now());
      if (path) {
        event.preventDefault();
        navigate(path);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [navigate]);

  // Register shortcuts for help display
  useEffect(() => {
    const shortcuts: KeyboardShortcut[] = [
      {
        key: '?',
        description: 'Show keyboard shortcuts',
        category: 'General',
        action: () => setHelpOpen(true),
      },
      {
        key: 'g d',
        description: 'Go to Dashboard',
        category: 'Navigation',
        action: () => navigate('/dashboard'),
      },
      {
        key: 'g i',
        description: 'Go to Inbox',
        category: 'Navigation',
        action: () => navigate('/inbox'),
      },
      {
        key: 'g t',
        description: 'Go to Tickets',
        category: 'Navigation',
        action: () => navigate('/tickets'),
      },
      {
        key: 'g c',
        description: 'Go to Customers',
        category: 'Navigation',
        action: () => navigate('/customers'),
      },
      {
        key: 'g k',
        description: 'Go to Knowledge Base',
        category: 'Navigation',
        action: () => navigate('/knowledge'),
      },
      {
        key: 'g s',
        description: 'Go to Settings',
        category: 'Navigation',
        action: () => navigate('/settings'),
      },
    ];

    shortcuts.forEach(registerShortcut);

    return () => {
      shortcuts.forEach((s) => unregisterShortcut(s.key));
    };
  }, [navigate, registerShortcut, unregisterShortcut, setHelpOpen]);

  return null;
}

function ShortcutsHelpDialog() {
  const { isHelpOpen, setHelpOpen, shortcuts } = useShortcutRegistry();

  // Close on escape
  useKeyboardShortcut('escape', () => setHelpOpen(false), { enabled: isHelpOpen });

  if (!isHelpOpen) return null;

  // Group shortcuts by category
  const groupedShortcuts = shortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = [];
      }
      acc[shortcut.category].push(shortcut);
      return acc;
    },
    {} as Record<string, KeyboardShortcut[]>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={() => setHelpOpen(false)} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Keyboard className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Keyboard Shortcuts</h2>
          </div>
          <button
            onClick={() => setHelpOpen(false)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category} className="mb-6 last:mb-0">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 dark:text-gray-400">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut) => (
                  <div
                    key={shortcut.key}
                    className="flex items-center justify-between py-2"
                  >
                    <span className="text-gray-700 dark:text-gray-200">{shortcut.description}</span>
                    <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono text-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700">
                      {shortcut.key}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {shortcuts.length === 0 && (
            <p className="text-gray-500 text-center py-8 dark:text-gray-400">No shortcuts registered</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <p className="text-sm text-gray-500 text-center dark:text-gray-400">
            Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs dark:bg-gray-700">Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
}
